import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import SeoHead from '@/components/SeoHead';
import ProductCard from '../components/ProductCard';
import { Input } from '../components/ui/input';
import { fetchProducts } from '../lib/firebase';
import { fetchProductsAdmin } from '../lib/firebaseAdmin';
import { productPropType } from '../lib/propTypes';
import { DEPARTMENT_DISPLAY_ORDER, getProductDepartment } from '../lib/productRoutes';

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.12,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: 'easeOut' },
  },
};

export default function Shop({ products = [] }) {
  const router = useRouter();
  const { category: qCat, focus } = router.query;

  const [catalogProducts, setCatalogProducts] = useState(products);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');

  useEffect(() => {
    setCatalogProducts(products);
  }, [products]);

  useEffect(() => {
    if (typeof qCat === 'string' && qCat) setCategory(qCat);
  }, [qCat]);

  useEffect(() => {
    let isMounted = true;

    fetchProducts({ forceFresh: true })
      .then((freshProducts) => {
        if (!isMounted || !Array.isArray(freshProducts)) return;
        setCatalogProducts(freshProducts);
      })
      .catch((error) => {
        console.warn('Could not refresh shop products on the client:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (focus !== 'search') return;

    const searchInput = document.getElementById('shop-search');
    searchInput?.focus();
    searchInput?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focus]);

  const filtered = catalogProducts
    .filter((product) => (product.name || '').toLowerCase().includes(search.toLowerCase()))
    .filter((product) => {
      if (category === 'Hot Sale') return Boolean(product.isHotSale);
      return category === 'All' || getProductDepartment(product) === category;
    });

  return (
    <div className="space-y-12 pb-10">
      <SeoHead
        title="Shop Footwear | Trendy Quest Collection Kenya"
        description="Browse the Trendy Quest catalog across men, women, kids, and clothes, then find the right pair or outfit for everyday comfort and style."
        path="/shop"
      />

      <section id="shop-catalog" className="w-full px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
          className="rounded-[2.2rem] border border-[#eadfc9] bg-white/90 p-5 shadow-[0_18px_50px_rgba(26,48,35,0.06)] sm:p-7"
        >
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-[#2b5f34]">Shop Catalog</p>
              <h2 className="mt-3 text-3xl font-playfair font-bold text-[#162218] sm:text-4xl">Browse the collection</h2>
              <p className="mt-2 max-w-2xl text-sm leading-7 text-[#62675f] sm:text-base">
                Filter by category or search for the pair that fits your style.
              </p>
            </div>

            <div className="rounded-full bg-[#f6f1e4] px-4 py-2 text-sm font-semibold text-[#35553a]">
              {filtered.length} product{filtered.length === 1 ? '' : 's'}
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-wrap gap-3">
              {['All', 'Hot Sale', ...DEPARTMENT_DISPLAY_ORDER].map((item) => (
                <motion.button
                  key={item}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setCategory(item)}
                  className={`rounded-full px-5 py-3 text-sm font-semibold transition ${
                    category === item
                      ? 'bg-[#173822] text-white shadow-[0_12px_24px_rgba(23,56,34,0.16)]'
                      : 'border border-[#e6dcc7] bg-white text-[#566258] hover:border-[#cdb98f] hover:bg-[#fffaf0]'
                  }`}
                >
                  {item}
                </motion.button>
              ))}
            </div>

            <div className="flex w-full flex-col gap-3 sm:flex-row xl:w-auto">
              <div className="relative min-w-0 flex-1 xl:w-[320px]">
                <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8d9386]" />
                <Input
                  id="shop-search"
                  placeholder="Search products..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  className="h-13 rounded-full border-[#e7dcc7] bg-white pl-12 pr-4 text-[15px] shadow-none focus:border-[#cbb27f]"
                />
              </div>
              <button
                type="button"
                onClick={() => setSearch('')}
                className="rounded-full border border-[#e4dac7] bg-white px-5 py-3 text-[#4c5b50] transition hover:bg-[#fff8ed]"
              >
                Reset
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={gridVariants}
          initial="hidden"
          animate="visible"
          className="mt-8 grid grid-cols-2 gap-3 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4"
        >
          {filtered.map((product) => (
            <motion.div key={product.id} variants={cardVariants}>
              <ProductCard product={product} />
            </motion.div>
          ))}
        </motion.div>

        {filtered.length === 0 && (
          <div className="mt-10 rounded-[2rem] border border-dashed border-[#d9ccb0] bg-[#fffaf1] px-6 py-12 text-center">
            <p className="text-lg font-semibold text-[#243626]">No products found.</p>
            <p className="mt-2 text-sm text-[#6a7067]">Try another search or switch to a different category.</p>
          </div>
        )}
      </section>
    </div>
  );
}

Shop.propTypes = {
  products: PropTypes.arrayOf(productPropType),
};

export async function getStaticProps() {
  try {
    const products = await fetchProductsAdmin();
    return {
      props: { products },
      revalidate: 60,
    };
  } catch (error) {
    console.error('Failed to fetch products:', error.message);
    return {
      props: { products: [] },
      revalidate: 60,
    };
  }
}
